import { useParams, Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import Breadcrumb from '@/components/base/Breadcrumb';
import type { Comic, Chapter } from '@/types';

function ChapterItem({ chapter, comicId }: { chapter: Chapter; comicId: number }) {
  return (
    <Link
      to={`/article/${comicId}`}
      className="flex items-center justify-between px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
          <i className="ri-booklet-line text-sm"></i>
        </div>
        <span className="text-sm font-medium text-foreground-800 line-clamp-1">{chapter.title}</span>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className="text-xs text-foreground-400 whitespace-nowrap">{chapter.date}</span>
        <i className="ri-arrow-right-s-line text-foreground-300"></i>
      </div>
    </Link>
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic: Comic | undefined = comics.find(c => c.id === Number(id));

  if (!comic) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </div>
          <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-2">漫画未找到</h2>
          <p className="text-sm text-foreground-500 mb-4">您要查看的漫画不存在或已下架</p>
          <Link to="/" className="inline-flex items-center gap-1 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const statusLabel = comic.status === 'ongoing' ? '连载中' : '已完结';
  const statusDot = comic.status === 'ongoing' ? 'bg-accent-500' : 'bg-secondary-500';

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - 韩漫在线免费阅读`,
            description: comic.description,
            author: { '@type': 'Person', name: comic.author },
            datePublished: comic.chapterList[comic.chapterList.length - 1]?.date,
            dateModified: comic.chapterList[0]?.date,
            image: comic.cover,
            publisher: {
              '@type': 'Organization',
              name: '韩漫屋',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmanwu.com/' },
              { '@type': 'ListItem', position: 2, name: '漫画详情', item: `https://www.hanmanwu.com/article/${comic.id}` },
              { '@type': 'ListItem', position: 3, name: comic.title },
            ],
          }),
        }}
      />
      <main className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: '漫画详情', href: '/category/all' },
            { label: comic.title },
          ]} />
        </div>

        <section className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-6 md:gap-10 mb-10">
            <div className="flex-shrink-0 w-full sm:w-[280px]">
              <div className="relative aspect-[3/4] rounded-lg overflow-hidden">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 韩漫在线免费阅读`}
                  title={`${comic.title} - 韩漫在线免费阅读`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-3 right-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === 'ongoing' ? 'bg-primary-500 text-background-50' : 'bg-secondary-500 text-background-50'
                  }`}>
                    {statusLabel}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="text-sm text-foreground-500">作者：<strong className="text-foreground-800 font-medium">{comic.author}</strong></span>
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500 text-sm"></i>
                  <span className="text-sm font-semibold text-foreground-800">{comic.rating}</span>
                  <span className="text-xs text-foreground-400">分</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-eye-line text-foreground-400 text-sm"></i>
                  <span className="text-sm text-foreground-600">{(comic.views / 10000).toFixed(0)}万阅读</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${statusDot}`}></span>
                  <span className="text-sm text-foreground-600">{statusLabel}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {comic.tags.map(tag => (
                  <span key={tag} className="px-3 py-1 bg-accent-50 text-accent-700 rounded-full text-xs font-medium whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 border border-background-200/70 rounded-lg p-4 mb-5">
                <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-2">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to={`/article/${comic.id}`}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line"></i>
                  开始阅读
                </Link>
                <button className="inline-flex items-center gap-2 px-6 py-3 border border-background-300/60 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line"></i>
                  收藏
                </button>
              </div>
            </div>
          </div>

          <section className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-heading font-bold text-foreground-950">章节列表</h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 bg-primary-500 text-background-50 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer">
                  正序
                </button>
                <span className="text-xs text-foreground-400">共 {comic.chapterList.length} 话</span>
              </div>
            </div>
            <div className="space-y-2">
              {comic.chapterList.map(chapter => (
                <ChapterItem key={chapter.id} chapter={chapter} comicId={comic.id} />
              ))}
            </div>
          </section>

          <section className="mb-10">
            <div className="bg-primary-50 border border-primary-200/50 rounded-lg p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                <i className="ri-book-open-line text-2xl"></i>
              </div>
              <h2 className="text-lg font-heading font-semibold text-foreground-950 mb-2">开始阅读《{comic.title}》</h2>
              <p className="text-sm text-foreground-500 mb-4">下拉式阅读体验，无删减原版内容</p>
              <Link
                to={`/article/${comic.id}`}
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line"></i>
                立即阅读
              </Link>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4" data-product-shop="">
              {comics
                .filter(c => c.category === comic.category && c.id !== comic.id)
                .slice(0, 6)
                .map(related => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={related.cover}
                        alt={related.title}
                        title={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-2 md:p-3">
                      <h3 className="text-xs md:text-sm font-heading font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {related.title}
                      </h3>
                      <div className="flex items-center gap-1 mt-1">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                        <span className="text-xs text-foreground-600">{related.rating}</span>
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </section>
        </section>
      </main>
    </>
  );
}